import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { PersonType } from '../../shared/models/quotation-model/catalogs/person-type.model';
import {
  isTypePersonPhysicalConst,
  isTypePersonPhysicalEmpActConst,
} from '../../shared/models/quotation-model/constants/constants-model';
import { InsertQuotation } from '../../shared/models/quotation-model/main/insert-quotation.model';
import { Quotation } from '../../shared/models/quotation-model/main/quotation.model';
import { EditClient } from '../../shared/models/quotation-model/main/user-update.model';
import { PersonTypeService } from '../../shared/services/quotation/catalogs/person-type.service';
import { EventEmiterService } from '../../shared/services/quotation/event-emiter.service';
import { QuotationService } from '../../shared/services/quotation/quotation.service';
import { MessageDialogComponent } from '../quotation-dialog/message-dialog/message-dialog.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { NgxMaskDirective } from 'ngx-mask';
import { MatInputModule } from '@angular/material/input';
import { GeneralService } from '../../shared/services/general.service';
import { InputGeneral } from '../../shared/inputs/input-general/input-general';

@Component({
  selector: 'app-client-section',
  templateUrl: './client-section.component.html',
  styleUrls: ['./client-section.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatFormField,
    MatSelect,
    MatLabel,
    MatOption,
    MatError,
    ReactiveFormsModule,
    MatInputModule,
    NgxMaskDirective,
    InputGeneral,
  ],
})
export class ClientSectionComponent implements OnInit {
  @Input() quotationId: any;
  @Input() ticketId!: number;

  @Output() summaryVisibilityReturn = new EventEmitter<any>();
  @Output() clientReturn = new EventEmitter<{
    customer: any;
    selectedPersonType: PersonType;
    quotationId: any;
  }>();

  loading: boolean = false;

  placeholderName: string = '';

  insertQuotationModel: InsertQuotation = {} as InsertQuotation;
  editUserItem: EditClient = {} as EditClient;
  quotation: Quotation = {} as Quotation;

  customer: any = {};
  selectedPersonType: PersonType = {} as PersonType;
  personTypes: PersonType[] = [];

  customerFormGroup!: FormGroup;

  constructor(
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private quotationService: QuotationService,
    private personTypeService: PersonTypeService,
    private translate: TranslateService,
    private generalService: GeneralService,
    private loader: EventEmiterService,
  ) {
    this.generalService.generalEvent.subscribe(() => {
      this.onSubmitCustomer();
    });
    this.customerFormGroup = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: [''],
      secondLastName: [''],
      phoneNumber: [''],
      personType: ['', Validators.required],
      email: ['', [Validators.email]],
      TIN: [
        '',
        [
          Validators.minLength(11),
          Validators.maxLength(13),
          Validators.pattern(
            /^[A-ZÑ&]{3,4}[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[A-Z0-9]{3}$/,
          ),
        ],
      ],
    });

    this.subsForm();
  }

  ngOnInit() {
    this.loadPersonTypes();
  }

  public subsForm() {
    this.customerFormGroup.valueChanges
      .pipe(debounceTime(700), distinctUntilChanged())
      .subscribe((value) => {
        // this.onSubmitCustomer();
        if (value.personType == 1 || value.personType == 2) {
          this.customerFormGroup.get('lastName')?.setValidators([Validators.required]);
          this.customerFormGroup.get('lastName')?.updateValueAndValidity();
          this.customerFormGroup.get('secondLastName')?.setValidators([Validators.required]);
          this.customerFormGroup.get('secondLastName')?.updateValueAndValidity();
        } else {
          this.customerFormGroup.get('lastName')?.clearValidators();
          this.customerFormGroup.get('lastName')?.updateValueAndValidity();
          this.customerFormGroup.get('secondLastName')?.clearValidators();
          this.customerFormGroup.get('secondLastName')?.updateValueAndValidity();
        }
      });
  }

  validateOption(options: any[], control: FormControl) {
    const selectedOption = control.value;
    if (!selectedOption || !options.some((obj) => obj.id === selectedOption)) {
      return { invalidOption: true };
    }
    return null;
  }

  //se va a todos los componentes
  async loadQuotation() {
    this.loader.isLoadingPage(true);
    var response = await firstValueFrom(
      this.quotationService.getQuotationFinancialValues(this.quotationId),
    );
    //se usa en client y summary
    this.selectedPersonType.description = response.personDescription;
    this.selectedPersonType.id = response.personId;
    //se usa sólo en client
    await this.loadPersonTypes();
    this.customerFormGroup.get('personType')!.disable();
    this.loadUserData(
      response.name,
      response.lastName,
      response.secondLastName,
      response.telephone,
      response.personId,
      response.email,
      response.tin,
    );
  }

  async onSubmitCustomer(auto: boolean = false) {
    if (!this.customerFormGroup.invalid) {
      this.customer.personTypeId = this.selectedPersonType.id;
      this.customer.firstName = this.customerFormGroup.get('firstName')?.value;
      this.customer.lastName = this.customerFormGroup.get('lastName')?.value;
      this.customer.secondLastName = this.customerFormGroup.get('secondLastName')?.value;
      this.customer.phoneNumber = this.customerFormGroup.get('phoneNumber')?.value;
      this.customer.email = this.customerFormGroup.get('email')?.value;
      this.customer.TIN = this.customerFormGroup.get('TIN')?.value;

      this.insertQuotationModel.email = this.customerFormGroup.get('email')?.value;
      this.insertQuotationModel.lastName = this.customerFormGroup.get('lastName')?.value;
      this.insertQuotationModel.motherLastName =
        this.customerFormGroup.get('secondLastName')?.value;
      this.insertQuotationModel.name = this.customerFormGroup.get('firstName')?.value;
      this.insertQuotationModel.TIN = this.customerFormGroup.get('TIN')?.value;
      this.insertQuotationModel.personTypeId = this.selectedPersonType.id;
      this.insertQuotationModel.phone = this.customerFormGroup.get('phoneNumber')?.value;
      if (this.quotationId === 0 && !this.loading) {
        this.loading = true;
        //se usa como promesa en lugar de subscribe para poder obtener el id de la cotización
        try {
          const response = await firstValueFrom(
            this.quotationService.createQuotation(this.insertQuotationModel),
          );
          this.quotationId = response;
        } catch (error) {
          console.error(error);
        } finally {
          this.customerFormGroup.get('personType')?.disable();
          this.customerFormGroup.get('firstName')?.disable();
          this.customerFormGroup.get('secondLastName')?.disable();
          this.customerFormGroup.get('lastName')?.disable();
          this.loading = false;
        }
      } else {
        this.customer.firstName = this.customerFormGroup.get('firstName')?.value;
        this.customer.lastName = this.customerFormGroup.get('lastName')?.value;
        this.customer.secondLastName = this.customerFormGroup.get('secondLastName')?.value;
        this.customer.phoneNumber = this.customerFormGroup.get('phoneNumber')?.value;
        this.customer.email = this.customerFormGroup.get('email')?.value;
        this.customer.TIN = this.customerFormGroup.get('TIN')?.value;
        this.updateUser(
          this.customer.firstName,
          this.customer.lastName,
          this.customer.secondLastName,
          this.customer.phoneNumber,
          this.customer.email,
          this.customer.TIN,
        );
      }
      this.sendClientToParent();
      return true;
    } else {
      if (auto) {
        this.loader.isLoadingPage(false);
        this.openDialogChangesWarning();
      }
      return false;
    }
  }

  updateOnChange() {
    if (this.quotationId > 0) this.onSubmitCustomer();
  }

  openDialogChangesWarning() {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '350px',
      data: this.translate.instant('quotation.messages.changesDetected'),
      panelClass: 'dialog-container-custom',
    });
  }

  //se va al componente client-section
  async onChangePersonType(event: any) {
    const option = event.source.selected;
    var oldId = this.selectedPersonType.id;
    this.selectedPersonType = { id: option.value, description: option.viewValue } as PersonType;

    if (this.customerFormGroup.get('personType')?.value == 3) {
      this.customerFormGroup.controls['lastName']!.disable();
      this.customerFormGroup.controls['secondLastName']!.disable();
    } else {
      this.customerFormGroup.controls['lastName']!.enable();
      this.customerFormGroup.controls['secondLastName']!.enable();
    }

    if (oldId != option.value && this.quotationId > 0) {
      this.quotation = {} as Quotation;
      this.quotation.customer = {} as any;
      this.quotation.customer.personTypeId = this.selectedPersonType.id;
      this.insertQuotationModel.email = this.customerFormGroup.get('email')?.value;
      this.insertQuotationModel.lastName = this.customerFormGroup.get('lastName')?.value;
      this.insertQuotationModel.motherLastName =
        this.customerFormGroup.get('secondLastName')?.value;
      this.insertQuotationModel.name = this.customerFormGroup.get('firstName')?.value;
      this.insertQuotationModel.personTypeId = this.selectedPersonType.id;
      this.insertQuotationModel.phone = this.customerFormGroup.get('phoneNumber')?.value;
      this.insertQuotationModel.TIN = this.customerFormGroup.get('TIN')?.value;
      try {
        // se agrego una condicion para que actualice al usuario en ves de crear otra cotizacion
        if (this.quotationId == 0 && !this.loading && this.customerFormGroup.valid) {
          this.loading = true;
          const response = await firstValueFrom(
            this.quotationService.createQuotation(this.insertQuotationModel),
          );
          this.quotationId = response;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.customerFormGroup.get('personType')?.disable();
        this.customerFormGroup.get('firstName')?.disable();
        this.customerFormGroup.get('secondLastName')?.disable();
        this.customerFormGroup.get('lastName')?.disable();

        this.loading = false;
      }
    }
    // this.onSubmitCustomer();
  }

  updateUser(name: any, lastname: any, motherLastName: any, phone: any, email: any, tin: string) {
    this.editUserItem = {
      quotationId: this.quotationId,
      name: name,
      lastName: lastname,
      motherLastName: motherLastName,
      phone,
      email: email,
      TIN: tin,
    };
    firstValueFrom(this.quotationService.updateUser(this.editUserItem));
  }

  async loadPersonTypes() {
    this.personTypes = await firstValueFrom(this.personTypeService.getPersonTypes());
  }

  loadUserData(
    firstNameObj: any,
    lastNameObj: any,
    secondLastNameObj: any,
    phoneNumberObj: any,
    personTypeObj: any,
    emailObj: any,
    tin: any,
  ) {
    this.customerFormGroup.patchValue({
      firstName: firstNameObj,
      lastName: lastNameObj,
      secondLastName: secondLastNameObj,
      phoneNumber: phoneNumberObj,
      personType: personTypeObj,
      email: emailObj,
      TIN: tin,
    });
  }

  isTypePersonPhysical() {
    return (
      this.selectedPersonType &&
      this.selectedPersonType.id &&
      (this.selectedPersonType.id === isTypePersonPhysicalConst ||
        this.selectedPersonType.id === isTypePersonPhysicalEmpActConst)
    );
  }

  sendClientToParent() {
    this.clientReturn.emit({
      customer: this.customer,
      selectedPersonType: this.selectedPersonType,
      quotationId: this.quotationId,
    });
  }
}
